// Đơn vị chưa có bảng giá / bảng chiết khấu (docs/KE_HOACH_BANG_GIA_KHI_TAO_DON_VI.md §4.5).
//  · Dải đầu mọi trang (khung): thiếu bảng giá — của chính mình hoặc cấp trên trong chuỗi — chặn lên đơn; CTV thiếu bảng
//    chiết khấu chỉ nhắc (vẫn lên đơn). Cho cả chủ lẫn nhân viên, cả hai giao diện.
//  · Đơn vị đang tạm khóa (nhà phân phối / cấp trên khoá) cũng chặn tạo đơn (máy chủ: unit.suspended) — cùng dải.
//  · Trang Tạo đơn / hộp Nhập Excel hiện trạng thái bị chặn thay cho form (máy chủ vẫn chặn — pricing.no_assignment).
//  · Đọc GET /auth/unit-access (`pricing`), đọc lại khi quay về tab — cấp trên vừa gán bảng là dải tự mất.

import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Info } from 'lucide-react'
import { get } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { cn } from '@/lib/utils'

export interface PricingStatus {
  /** Đơn vị đầu tiên trong chuỗi (mình → cấp trên) chưa có bảng giá — null = lên đơn được */
  blockedBy: { shopId: string; name: string; self: boolean; contact: string } | null
  /** CTV chưa có bảng chiết khấu của cấp trên hiện tại */
  schemeMissing: boolean
  /** Cấp trên trực tiếp (trực thuộc NPP → tên NPP) */
  contact: string
  /** Đang tạm khóa — `contact`: người khoá (cấp trên hoặc nhà phân phối). Máy chủ cũ không có ô này. */
  suspended?: { contact: string } | null
}

export function usePricingStatus(enabled = true) {
  const { activeShopId, isAuthenticated } = useAuth()
  return useQuery({
    queryKey: ['unit-access', 'pricing', activeShopId],
    queryFn: async () => (await get<{ pricing?: PricingStatus }>('/auth/unit-access')).pricing ?? null,
    enabled: enabled && isAuthenticated && !!activeShopId,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  })
}

export function pricingBlockText(b: NonNullable<PricingStatus['blockedBy']>): string {
  return b.self ? `Chưa có bảng giá — chưa tạo được đơn. Liên hệ ${b.contact}.` : `${b.name} chưa có bảng giá — chưa tạo được đơn.`
}

export function suspendedText(contact: string): string {
  return `Đơn vị đang bị tạm khóa — chưa tạo được đơn. Liên hệ ${contact}.`
}

export function schemeMissingText(contact: string): string {
  return `Chưa có bảng chiết khấu — chưa được tính chiết khấu. Liên hệ ${contact}.`
}

/** Dải đầu trang của khung. `enabled = false` ở khu NPP / tài khoản quản trị. */
export function PricingBanner({ enabled = true, className }: { enabled?: boolean; className?: string }) {
  const { data } = usePricingStatus(enabled)
  if (!enabled || !data || (!data.blockedBy && !data.schemeMissing && !data.suspended)) return null
  return (
    <div className={cn('space-y-2', className)} data-pricing-banner="">
      {data.suspended && (
        <p role="status" className="flex items-start gap-2 rounded-xl border border-danger/30 bg-danger/5 px-3.5 py-2.5 text-sm font-medium text-ink">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-danger" aria-hidden />
          <span>{suspendedText(data.suspended.contact)}</span>
        </p>
      )}
      {data.blockedBy && (
        <p role="status" className="flex items-start gap-2 rounded-xl border border-warn/40 bg-warn/10 px-3.5 py-2.5 text-sm font-medium text-ink">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" aria-hidden />
          <span>{pricingBlockText(data.blockedBy)}</span>
        </p>
      )}
      {data.schemeMissing && (
        <p role="status" className="flex items-start gap-2 rounded-xl border border-rule bg-paperSoft px-3.5 py-2.5 text-sm text-ink">
          <Info className="mt-0.5 size-4 shrink-0 text-inkSoft" aria-hidden />
          <span>{schemeMissingText(data.contact)}</span>
        </p>
      )}
    </div>
  )
}

/**
 * Lý do không tạo được đơn (đang tạm khóa, rồi thiếu bảng giá) — thay cho form tạo đơn / nhập Excel. null = không chặn
 * (hoặc chưa biết — máy chủ vẫn chặn).
 */
export function useOrderBlock(): string | null {
  const d = usePricingStatus().data
  if (!d) return null
  if (d.suspended) return suspendedText(d.suspended.contact)
  return d.blockedBy ? pricingBlockText(d.blockedBy) : null
}

export function PricingBlocked({ text, className }: { text: string; className?: string }) {
  return (
    <div role="alert" data-pricing-blocked="" className={cn('flex flex-col items-center gap-3 rounded-xl bg-raise px-6 py-10 text-center shadow-card', className)}>
      <span className="grid size-12 place-items-center rounded-full bg-warn/10 text-warn">
        <AlertTriangle className="size-6" aria-hidden />
      </span>
      <p className="max-w-md text-base font-medium text-ink">{text}</p>
    </div>
  )
}

/** Tài khoản đơn vị (Đại lý / CTV / Shop) — khu NPP, tài khoản quản trị không có dải. */
export const isUnitType = (t: string | null | undefined): boolean => t === 'agency' || t === 'ctv' || t === 'shop'
